import React from "react";
import styled, { useTheme, createGlobalStyle } from "styled-components";

const GlobalStyle = createGlobalStyle`
  body {
    font-family: 'Noto Music', sans-serif;
    margin: 0;
    padding: 0;
  }
`;

const Container = styled.div`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(
    circle at 50% -20%,
    ${({ theme }) => theme.colors.highlight} 0%,
    ${({ theme }) => theme.colors.accent} 35%,
    #2C1B3D 65%,
    ${({ theme }) => theme.colors.background} 80%
  );
`;

const TitleContainer = styled.div`
  display: grid;
  position: relative;
  pointer-events: none;
`;

const LogoImage = styled.img`
  max-width: 80%;
  max-height: 60vh;
  margin: 0 auto;
  display: block;
  pointer-events: none;
`;

const TitleBase = styled.h1`
  font-family: 'Noto Music', sans-serif;
  font-size: 8rem;
  color: white;
  display: flex;
  align-items: center;
  grid-area: 1/1/-1/-1;
  margin: 0;
    pointer-events: none;
`;

const TitleTop = styled(TitleBase)`
  clip-path: polygon(0% 0%, 100% 0%, 100% 48%, 0% 58%);
    pointer-events: none;
`;

const ReverseMixIn = styled.span`
  display: inline-block;
  transform: scaleX(-1);
  position: relative;
  z-index: 1;
  pointer-events: none;
`;

const TitleBottom = styled(TitleBase)`
  clip-path: polygon(0% 60%, 100% 50%, 100% 100%, 0% 100%);
  color: transparent;
  background: linear-gradient(177deg, rgba(0,0,0,0.7) 53%, white 65%);
  -webkit-background-clip: text;
  background-clip: text;
  transform: translateX(-0.02em);
  pointer-events: none;
  
  ${ReverseMixIn} {
    background: inherit;
    -webkit-background-clip: text;
    background-clip: text;
  }
`;

const ContentSection = styled.section`
  min-height: 100vh;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background};
  padding: 4rem 2rem;
`;

const ContentContainer = styled.div`
  width: 100%;
  padding: 0 1rem; // Smaller padding on mobile
  margin: 0 auto;
  
  @media (min-width: 480px) {
    padding: 0 1.5rem; // Larger padding on wider screens
    max-width: 430px;
  }
`;

const PixelWrapper = styled.div`
  position: relative;
  
  pixel-canvas {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    opacity: 0.8;
  }
  
  > * {
    position: relative;
    z-index: 1;
  }
`;

// Typography Components
const Title = styled.h2`
  font-family: 'Noto Music', sans-serif;
  font-size: ${props => props.size || '3rem'};
  color: white;
  margin-bottom: 2.5rem;
  line-height: 1.1;
  position: relative;
  
  ${props => props.withFlowingPrefix && `
    &::before {
      content: '//';
      position: absolute;
      left: -0.8em;
      top: 0;
      color: rgba(255, 255, 255, 0.4);
      pointer-events: none;
    }
  `}
`;

const Subtitle = styled.h3`
  font-family: 'Noto Music', sans-serif;
  font-size: ${props => props.size || '1.5rem'};
  color: white;
  margin-bottom: 1rem;
`;

const BodyText = styled.p`
  font-family: 'Noto Music', sans-serif;
  font-size: ${props => props.size || '1.2rem'};
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.65;
  padding-bottom: 1.2rem;
`;

const StyledList = styled.ul`
  padding-left: 3rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.2rem;
  font-family: 'Noto Music', sans-serif;
  line-height: 1.65;
  padding-bottom: 1.2rem;
`;

const SubEntryList = styled(StyledList)`
  padding-left: 1.5rem;
  margin-top: 0.5rem;
  
  li {
    font-size: 1rem;
    opacity: 0.85;
  }
`;

const AccentText = styled.span`
  color: ${({ theme }) => theme.colors.accent};
`;

const LinkText = styled.a`
  text-decoration: underline;
  font-weight: bold;
  color: inherit;
  transition: color 0.2s;

  &:visited, &:hover, &:active, &:link {
    color: inherit;
  }
`;

const Spacer = styled.div`
  height: ${props => props.size || '1.5rem'};
  width: 100%;
`;

const StyledButton = styled.a`
  display: inline-block;
  padding: 0.8rem 1.8rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  color: white;
  font-family: 'Noto Music', sans-serif;
  font-size: 1.1rem;
  font-weight: 500;
  text-decoration: none;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25), inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  opacity: ${props => props.disabled ? 0.5 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(${({ theme }) => theme.colors.accent.replace(/[^\d,]/g, '')}, 0.2), 
      transparent
    );
    transform: translateX(-100%);
    transition: transform 0.8s ease;
  }
  
  &:hover {
    background: ${props => props.disabled ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.15)'};
    border-color: ${props => props.disabled ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.3)'};
    transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.disabled ? 
      '0 4px 20px rgba(0, 0, 0, 0.25), inset 0 0 0 1px rgba(255, 255, 255, 0.1)' : 
      '0 8px 25px rgba(0, 0, 0, 0.3), inset 0 0 0 1px rgba(255, 255, 255, 0.2)'};
    
    &::before {
      transform: ${props => props.disabled ? 'translateX(-100%)' : 'translateX(100%)'};
    }
  }
  
  &:active {
    transform: ${props => props.disabled ? 'none' : 'translateY(1px)'};
    box-shadow: ${props => props.disabled ? 
      '0 4px 20px rgba(0, 0, 0, 0.25), inset 0 0 0 1px rgba(255, 255, 255, 0.1)' : 
      '0 3px 10px rgba(0, 0, 0, 0.3)'};
  }
`;

const BrandLogosContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  margin: 1rem 0;
`;

const BrandLink = styled.a`
  text-decoration: none;
  color: inherit;
  display: block;
  width: ${props => props.width || '80px'};
  height: ${props => props.height || '80px'};
`;

const BrandLogo = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  overflow: hidden;
  box-sizing: border-box;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, 
      rgba(255, 255, 255, 0.1) 0%, 
      rgba(255, 255, 255, 0.05) 50%, 
      rgba(255, 255, 255, 0.02) 100%
    );
    border-radius: 14px;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-4px) scale(1.05);
    border-color: rgba(255, 255, 255, 0.25);
    background: rgba(255, 255, 255, 0.08);
    box-shadow: 
      0 8px 32px rgba(0, 0, 0, 0.3),
      0 4px 16px rgba(255, 255, 255, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    
    &::before {
      opacity: 1;
    }
    
    img {
      transform: scale(1.1);
    }
  }
  
  &:active {
    transform: translateY(-2px) scale(1.02);
  }
`;

const BrandImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  filter: brightness(0.9) contrast(1.1);
  user-select: none;
  pointer-events: none;
  
  &:hover {
    filter: brightness(1) contrast(1.2);
  }
`;

const BrandLogos = ({ logoWidth = '45%', logoHeight = '80px' }) => {
  const brands = [
    { name: 'MEXC', src: '/asset/img/jun_2025/mexc.png', url: 'https://mexc.com/' },
    { name: 'ioBeacon', src: '/asset/img/jun_2025/iobeacon.png', url: 'https://iobeacon.org/' },
    { name: 'PicCollage', src: '/asset/img/jun_2025/piccollage.webp', url: 'https://piccollage.com/' },
    { name: 'Polygon', src: '/asset/img/jun_2025/polygon.png', url: 'https://polygon.technology/' },
    { name: 'Vircadia', src: '/asset/img/jun_2025/vircadia.png', url: 'https://vircadia.com/' }
  ];

  return (
    <BrandLogosContainer>
      {brands.map((brand) => (
        <BrandLink 
          key={brand.name} 
          href={brand.url} 
          target="_blank" 
          rel="noopener noreferrer"
          width={logoWidth}
          height={logoHeight}
        >
          <BrandLogo>
            <BrandImage 
              src={brand.src} 
              alt={`${brand.name} logo`}
              loading="lazy"
            />
          </BrandLogo>
        </BrandLink>
      ))}
    </BrandLogosContainer>
  );
};

// Team Member Components
const TeamMembersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1.5rem;
  margin: 1.5rem 0;
  justify-items: center;
`;

const TeamMemberCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem 1rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, 
      rgba(255, 255, 255, 0.1) 0%, 
      rgba(255, 255, 255, 0.05) 50%, 
      rgba(255, 255, 255, 0.02) 100%
    );
    border-radius: 14px;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-4px) scale(1.05);
    border-color: rgba(255, 255, 255, 0.25);
    background: rgba(255, 255, 255, 0.08);
    box-shadow: 
      0 8px 32px rgba(0, 0, 0, 0.3),
      0 4px 16px rgba(255, 255, 255, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    
    &::before {
      opacity: 1;
    }
  }
  
  &:active {
    transform: translateY(-2px) scale(1.02);
  }
`;

const TeamMemberAvatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${props => props.bgColor || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  overflow: hidden;
  border: 3px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  span {
    font-family: 'Noto Music', sans-serif;
    font-size: 1.8rem;
    font-weight: bold;
    color: white;
    text-transform: uppercase;
  }
  
  ${TeamMemberCard}:hover & {
    transform: scale(1.1);
    border-color: rgba(255, 255, 255, 0.4);
    
    img {
      transform: scale(1.1);
    }
  }
`;

const TeamMemberName = styled.h4`
  font-family: 'Noto Music', sans-serif;
  font-size: 1.1rem;
  color: white;
  margin: 0;
  text-align: center;
  font-weight: 500;
  letter-spacing: 0.5px;
`;

const TeamMembers = () => {
  const teamMembers = [
    { 
      name: 'Michael', 
      avatar: '/asset/img/team/michael.png',
      initials: 'M',
      bgColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      link: 'https://t.me/revofire'
    },
    { 
      name: 'Paddy', 
      avatar: '/asset/img/team/paddy.png',
      initials: 'P',
      bgColor: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      link: 'https://t.me/bricsHQ'
    },
    { 
      name: 'Tanya', 
      initials: 'T',
      bgColor: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      link: 'https://www.instagram.com/oh_bebegirl/'
    }
  ];

  return (
    <TeamMembersGrid>
      {teamMembers.map((member) => (
        <a 
          key={member.name} 
          href={member.link} 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          <TeamMemberCard>
            <TeamMemberAvatar bgColor={member.bgColor}>
              {member.avatar ? (
                <img src={member.avatar} alt={`${member.name} profile`} />
              ) : (
                <span>{member.initials}</span>
              )}
            </TeamMemberAvatar>
            <TeamMemberName>{member.name}</TeamMemberName>
          </TeamMemberCard>
        </a>
      ))}
    </TeamMembersGrid>
  );
};

function AppContent() {
  return (
    <>
      <GlobalStyle />
      <Container>
        <TitleContainer>
          <LogoImage src="/logo_trans.png" alt="ED3N Logo" />
        </TitleContainer>
      </Container>
      <ContentSection>
        <ContentContainer>
          <Title withFlowingPrefix>
            {"past events"}
          </Title>
          <PixelWrapper>
            <pixel-canvas
              data-gap="10"
              data-speed="25"
              data-colors="#ff6b6b, #feca57, #ff9ff3"
            />
            <Subtitle>
              <AccentText>{"gaming hackathon ft. mexc, ioBeacon, piccollage, polygon, vircadia"}</AccentText>
            </Subtitle>
            <BodyText>
              Jun. 8 - 22, 2025
            </BodyText>
            <BrandLogos />
            <StyledButton href="https://drive.google.com/drive/folders/1zfKCbADUQP4SCS-oaw145jv1Lq6BEFwI?usp=drive_link" target="_blank" rel="noopener noreferrer">
              photos
            </StyledButton>
          </PixelWrapper>
          <Spacer />
          <Spacer />
          <Spacer />
          <PixelWrapper>
            <pixel-canvas
              data-gap="10"
              data-speed="25"
              data-colors="#a29bfe, #6c5ce7, #fd79a8"
            />
            <Subtitle>
              <AccentText>{"polygon demo day"}</AccentText>
            </Subtitle>
            <BodyText>
              Apr. 6, 2025
            </BodyText>
            <StyledButton href="https://drive.google.com/drive/folders/1bQJUPyukVnnNCGEH9wAI0WTJUJid3Iag?usp=sharing" target="_blank" rel="noopener noreferrer">
              Photos
            </StyledButton>
          </PixelWrapper>
          <Spacer />
          <Spacer />
          <Spacer />
          <PixelWrapper>
            <pixel-canvas
              data-gap="10"
              data-speed="25"
              data-colors="#00b894, #00cec9, #55efc4"
            />
            <Subtitle>
              <AccentText>{"hack social"}</AccentText>
            </Subtitle>
            <BodyText>
              Mar. 27, 2025
            </BodyText>
            <StyledButton href="https://drive.google.com/drive/folders/1LcROn7a55hKHLgNeKyyb8mPNc__y4Ie1?usp=sharing" target="_blank" rel="noopener noreferrer">
              Photos
            </StyledButton>
          </PixelWrapper>
          <Spacer />
          <Spacer />
          <Spacer />
          <PixelWrapper>
            <pixel-canvas
              data-gap="10"
              data-speed="25"
              data-colors="#fab1a0, #e17055, #d63031"
            />
            <Subtitle>
              <AccentText>{"ai & xr social"}</AccentText>
            </Subtitle>
            <BodyText>
              Apr. 15, 2023
            </BodyText>
            <StyledButton href="https://www.instagram.com/p/CrD4laHrQBe/?img_index=1" target="_blank" rel="noopener noreferrer">
              Photos
            </StyledButton>
          </PixelWrapper>
          <Spacer />
          <Spacer />
          <Spacer />
          <PixelWrapper>
            <pixel-canvas
              data-gap="10"
              data-speed="25"
              data-colors="#74b9ff, #0984e3, #6c5ce7"
            />
            <Subtitle>
              <AccentText>{"ai & xr social"}</AccentText>
            </Subtitle>
            <BodyText>
              Mar. 18, 2023
            </BodyText>
            <StyledButton href="https://www.instagram.com/p/CqGm3MGvstt/?img_index=1" target="_blank" rel="noopener noreferrer">
              Photos
            </StyledButton>
          </PixelWrapper>
          <Spacer />
          <Spacer />
          <Spacer />
          <PixelWrapper>
            <pixel-canvas
              data-gap="10"
              data-speed="25"
              data-colors="#fd79a8, #e84393, #a29bfe"
            />
            <Subtitle>
              <AccentText>{"ai & xr social"}</AccentText>
            </Subtitle>
            <BodyText>
              Feb. 10, 2023
            </BodyText>
            <StyledButton href="https://www.instagram.com/p/CojLDsrpyqo/?img_index=1" target="_blank" rel="noopener noreferrer">
              Photos
            </StyledButton>
          </PixelWrapper>
          <Spacer />
          <Spacer />
          <Spacer />
          <PixelWrapper>
            <pixel-canvas
              data-gap="10"
              data-speed="25"
              data-colors="#0D2B16, #0FFF95, #00FF41"
            />
            <Title withFlowingPrefix>
              {"who are we?"}
            </Title>
            <BodyText>
              we build deep tech and connect likeminded people.
            </BodyText>
            <TeamMembers />
            <BodyText>
              connect with us on <LinkText href="https://x.com/ed3n_foundries">x</LinkText>, <LinkText href="https://t.me/+WIzpgmQJT7kzMjY1">telegram</LinkText>, and <LinkText href="https://www.instagram.com/ed3n_foundries/">instagram</LinkText>.
            </BodyText>
          </PixelWrapper>
        </ContentContainer>
      </ContentSection>
    </>
  );
}

export default AppContent;
